import * as THREE from 'three';

/**
 * TOON OUTLINES — inverted hull.
 * For each shaded mesh we add a twin drawn with BackSide faces, pushed out
 * radially in the vertex shader (three.js injects `instanceMatrix` and the
 * USE_INSTANCING define for InstancedMesh automatically) by `width` (world units, so the line
 * has a constant thickness independent of object scale — we divide by the
 * instance scale that `modelMatrix` carries). Instanced twins share the
 * source mesh's `instanceMatrix` buffer, so there is zero per-frame cost
 * beyond one extra draw call per shape.
 */
export const OUTLINE_WIDTH = 0.05;

export function outlineMaterial(color = 0x06121a, width = OUTLINE_WIDTH): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color(color) }, uWidth: { value: width } },
    vertexShader: /* glsl */ `
      uniform float uWidth;
      void main() {
        mat4 mm = modelMatrix;
        #ifdef USE_INSTANCING
        mm = modelMatrix * instanceMatrix;
        #endif
        // per-axis scale of the model matrix, to keep the hull width constant in world space
        vec3 sc = vec3(length(mm[0].xyz), length(mm[1].xyz), length(mm[2].xyz));
        // expand radially from the geometry origin rather than along face normals:
        // our primitives are flat-shaded (split normals), which would crack at edges
        float pl = length(position);
        vec3 dir = pl > 1e-5 ? position / pl : normalize(normal);
        vec3 p = position + dir * (uWidth / max(sc, vec3(1e-4)));
        gl_Position = projectionMatrix * viewMatrix * mm * vec4(p, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      void main() { gl_FragColor = vec4(uColor, 1.0); }
    `,
    side: THREE.BackSide,
    depthWrite: true,
  });
}

/** Twin for an InstancedMesh: shares the instanceMatrix buffer; caller syncs `count`. */
export function instancedOutline(src: THREE.InstancedMesh, width = OUTLINE_WIDTH): THREE.InstancedMesh {
  const twin = new THREE.InstancedMesh(src.geometry, outlineMaterial(0x06121a, width), src.count || 1);
  twin.instanceMatrix = src.instanceMatrix; // shared buffer
  twin.frustumCulled = false;
  twin.count = 0;
  twin.renderOrder = -1;
  return twin;
}

/** Twin for a plain Mesh: parented to the same object so it follows automatically. */
export function meshOutline(src: THREE.Mesh, width = OUTLINE_WIDTH): THREE.Mesh {
  const twin = new THREE.Mesh(src.geometry, outlineMaterial(0x06121a, width));
  twin.renderOrder = -1;
  src.add(twin);
  return twin;
}
